import contentfulClient from '@/utils/contentful/client'
import { mapCollection } from '@/utils/contentful/mapCollection'

import logger from '../utils/logger'

const getAlbumSysId = async (
	number: number,
	locale: string
): Promise<string> => {
	let response: Response

	try {
		response = await contentfulClient(
			`
			albumCollection(where: { OR: [{id: ${number}}] }, locale: "${locale}") {
				items {
					sys {
						id
					}
				}
			}
		`
		)

		const result = await response.json()

		const albumSysId = result.data.albumCollection.items[0].sys.id

		return albumSysId
	} catch (error) {
		logger('Error retrieving album data from Contentful API', error, {
			errorType: 'Contentful API',
			fetchUrl: response?.url,
			fetchStatus: response?.status,
		})
	}
}

const getBasicCollectionFromAlbumSysId = async (
	albumSysId: string,
	locale: string
): Promise<Collection> => {
	try {
		const query = `
			collectionCollection(where: { albums: { sys: { id: "${albumSysId}" } } }, limit: 1, locale: "${locale}") {
				items {
					id
					title
					thumbnailSquare {
						url
					}
				}
			}
		`

		const response = await contentfulClient(query)

		const contentfulData = await response.json()

		const contenfulCollection =
			contentfulData.data.collectionCollection.items[0]

		if (!contenfulCollection) {
			return null
		}

		const collection = mapCollection(contenfulCollection)

		return collection
	} catch (error) {
		logger('Error retrieving album collection data from Contentful', error, {
			errorType: 'Contentful',
		})

		return null
	}
}

const getPlaylistFromTrackId = async (
	trackId: number,
	locale: string
): Promise<Playlist> => {
	try {
		const response: Response = await contentfulClient(
			`
			playlistCollection(where: {trackIds_contains: "${trackId}"}, locale: "${locale}", limit: 1) {
				items {
					title,
					urlSlug,
					thumbnail {
						url
					}
				}
			}
		`
		)

		const result = await response.json()

		const playlist = result.data.playlistCollection.items[0]

		return playlist ?? null
	} catch (error) {
		logger('Error retrieving playlist content', error, {
			errorType: 'Contentful',
			contentType: 'playlist',
			contentId: trackId,
		})

		return null
	}
}

export const clientGetTrackFeaturedIn = async (
	track: Track,
	locale: string
): Promise<{ collection: Collection; playlist: Playlist }> => {
	const { album } = track

	const albumSysId = await getAlbumSysId(album.number, locale)

	const collection = albumSysId
		? await getBasicCollectionFromAlbumSysId(albumSysId, locale)
		: null

	const playlist = await getPlaylistFromTrackId(track.id, locale)

	return {
		collection,
		playlist,
	}
}
